// MACHINERY V2 (Stroje) — Diely a materiál: the QUERY, the SELECTION and the
// guard (M2.5; split out of PartsTabV2.tsx at the M2.5 review for the 300-line
// cap).
//
// PartsTabV2.tsx keeps the states and the pixels; this file owns what is asked
// of the server, which machine the answer belongs to, and which rows are ticked.
// Mutations stay in usePartsTabActions.ts.
//
// THE IDENTITY GUARD (review finding R1). `customerMachine.installedStockItems`
// is app-wide and nothing clears it on mount, so between navigating machine
// A -> B and B's response landing, the tab rendered A's parts under B's hero —
// with the unlink action live on every one of them. `list` below is therefore
// empty unless `useMachineScope` says the shared slot provably holds THIS
// machine's response; see that file for the reasoning and the two precedents
// (MachineProfileV2.tsx:137-142, SpecificationsTabV2.tsx:84). It is the REQUIRED
// pattern for any v2 surface reading an app-wide slice keyed by machine.
//
// A SELECTION MAY NEVER OUTLIVE THE ROWS IT POINTS AT (review finding C1, HIGH).
// `selected` used to survive every re-fetch: tick three parts, type in the
// search box, and the list narrowed to one unrelated row while the bulk bar
// still read "3 selected" and unlink destroyed three parts the user could no
// longer see, behind a confirm that only named a count. Two changes close it:
//   1. The prune effect below intersects `selected` with the newest list. It
//      hangs off `list` rather than off the fetch callback because EVERY path
//      that changes the rows (search, category, brand, sort, a mutation reload,
//      the machine change, the identity guard itself) ends there, and React
//      flushes it before paint — the bulk bar can never be clicked while it
//      still counts a row that is gone.
//   2. `allSelected` compares MEMBERSHIP, not counts. `selected.length ===
//      list.length` could read "all selected" while none of the visible rows
//      were ticked, and the header checkbox then CLEARED instead of selecting.
//
// SEARCH / CATEGORY / BRAND STAY SERVER-SIDE, exactly as v1 sends them; only the
// selection is client state. `queryRef` mirrors the query so the debounced
// closure reads the LATEST one instead of the one captured at keystroke time.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../../shared/hooks/sharedHooks";
import {
  changeInstalledStockItemsQuery,
  getCategories,
  getInstalledStockItems,
  getManufacturers,
} from "../../../customerMachineSlice";
import {
  InstalledStockItem,
  InstalledStockItemsQuery,
  InstalledStockItemsQueryParameter,
} from "../../../../inventory/models/models";
import { useMachineScope } from "./useMachineScope";

const SEARCH_DEBOUNCE_MS = 300;

export interface PartsTabQuery {
  query: InstalledStockItemsQuery;
  search: string;
  /** THIS machine's installed items — empty while the slot holds another's. */
  list: InstalledStockItem[];
  /** The slot provably holds this machine's response (its fetch settled). */
  ready: boolean;
  loading: boolean;
  failed: boolean;
  selected: string[];
  allSelected: boolean;
  fetchWith: (next: InstalledStockItemsQuery) => void;
  reload: () => void;
  handleSearch: (value: string) => void;
  handleSort: (parameter: InstalledStockItemsQueryParameter) => void;
  clearSearch: () => void;
  toggleAll: () => void;
  toggleOne: (id: string) => void;
  clearSelection: () => void;
}

export const usePartsTabQuery = (machineId: string): PartsTabQuery => {
  const dispatch = useAppDispatch();
  const items = useAppSelector((s) => s.customerMachine.installedStockItems);
  const loading = useAppSelector((s) => s.customerMachine.installedStockItemsLoading);
  const { owns, failed, track } = useMachineScope(machineId);

  const [query, setQuery] = useState<InstalledStockItemsQuery>({
    machineId,
    sortBy: InstalledStockItemsQueryParameter.number,
  });
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Mirrors `query` for the callbacks that must read the LATEST one without
   *  being rebuilt by it (reload, the search debounce). Written in an effect,
   *  never during render. */
  const queryRef = useRef(query);
  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  const fetchWith = useCallback(
    (next: InstalledStockItemsQuery) => {
      setQuery(next);
      dispatch(changeInstalledStockItemsQuery(next));
      // Attributed to the machine the REQUEST was for, never to whichever
      // machine is on screen when it lands.
      track(next.machineId, dispatch(getInstalledStockItems(next)).unwrap());
    },
    [dispatch, track]
  );

  const reload = useCallback(() => fetchWith(queryRef.current), [fetchWith]);
  const clearSelection = useCallback(() => setSelected([]), []);

  useEffect(() => {
    setSearch("");
    setSelected([]);
    dispatch(getCategories());
    dispatch(getManufacturers());
    fetchWith({ machineId, sortBy: InstalledStockItemsQueryParameter.number });
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [machineId, dispatch, fetchWith]);

  const list = useMemo(() => (owns && items ? items : []), [owns, items]);

  // C1: intersect the selection with the rows that are actually on screen.
  useEffect(() => {
    setSelected((prev) => {
      if (prev.length === 0) return prev;
      const next = prev.filter((id) => list.some((item) => item.id === id));
      return next.length === prev.length ? prev : next;
    });
  }, [list]);

  const handleSearch = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(
      () => fetchWith({ ...queryRef.current, search: value || undefined }),
      SEARCH_DEBOUNCE_MS
    );
  };

  const handleSort = (parameter: InstalledStockItemsQueryParameter) => {
    const sortDesc = query.sortBy !== parameter ? false : !query.sortDesc;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    fetchWith({ ...query, sortBy: parameter, sortDesc });
  };

  const clearSearch = () => {
    setSearch("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    fetchWith({ machineId, sortBy: query.sortBy, sortDesc: query.sortDesc });
  };

  const allSelected = list.length > 0 && list.every((item) => selected.indexOf(item.id) >= 0);

  const toggleAll = () => setSelected(allSelected ? [] : list.map((item) => item.id));
  const toggleOne = (id: string) =>
    setSelected((prev) => (prev.indexOf(id) >= 0 ? prev.filter((x) => x !== id) : [...prev, id]));

  return {
    query,
    search,
    list,
    ready: owns,
    loading,
    failed,
    selected,
    allSelected,
    fetchWith,
    reload,
    handleSearch,
    handleSort,
    clearSearch,
    toggleAll,
    toggleOne,
    clearSelection,
  };
};

export default usePartsTabQuery;
